import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, BookMarked, Download, Loader2, Pencil, Trash2, Upload } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import type { DesignerStandardItem, DesignerStandardSubfolder } from '../../types';
import {
  deleteDesignerStandard,
  designerStandardsInSubfolder,
  moveDesignerStandard,
  renameDesignerStandard,
  reorderDesignerStandards,
  subscribeDesignerStandards,
  uploadDesignerStandard,
} from '../../utils/designerStandardsService';
import { DESIGNER_STANDARD_SUBFOLDERS } from '../../utils/projectFileFolders';
import { canAccessProjectHubClientPortalControls } from '../../utils/userRoles';
import './DesignerStandardsSettingsCard.css';

const SUBFOLDER_HINT: Record<DesignerStandardSubfolder, string> = {
  sops: 'Standard operating procedures designers follow on every job.',
  template_2d: 'Cadwork .2d sheet templates (title blocks, layers, dimension styles).',
  template_3d: 'Cadwork .3d starting models and attribute presets.',
  timber_list_export: 'The timber-list export preset / mapping designers use for CSVs.',
};

function formatSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface DesignerStandardsSettingsCardProps {
  /** Optional extra actions rendered under the description (e.g. "Reset Files walkthrough"). */
  footer?: React.ReactNode;
}

const DesignerStandardsSettingsCard: React.FC<DesignerStandardsSettingsCardProps> = ({ footer }) => {
  const { user } = useAuth();
  const canEdit = canAccessProjectHubClientPortalControls(user?.role, user?.designations);
  const [items, setItems] = useState<DesignerStandardItem[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [uploadingTo, setUploadingTo] = useState<DesignerStandardSubfolder | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [dropTarget, setDropTarget] = useState<DesignerStandardSubfolder | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const inputRefs = useRef<Partial<Record<DesignerStandardSubfolder, HTMLInputElement | null>>>({});

  useEffect(() => {
    return subscribeDesignerStandards(
      list => {
        setItems(list);
        setLoadError(null);
      },
      err => {
        console.error('[designerStandards] subscribe', err);
        setLoadError('Could not load the Designer Standards library.');
      },
    );
  }, []);

  useEffect(() => {
    if (!status) return;
    const t = window.setTimeout(() => setStatus(null), 2500);
    return () => window.clearTimeout(t);
  }, [status]);

  const total = items.length;
  const bySub = useMemo(
    () =>
      DESIGNER_STANDARD_SUBFOLDERS.map(s => ({
        ...s,
        items: designerStandardsInSubfolder(items, s.id),
      })),
    [items],
  );

  const uploadFiles = async (subfolder: DesignerStandardSubfolder, files: File[]) => {
    if (!canEdit || !user?.id || files.length === 0) return;
    setUploadingTo(subfolder);
    setUploadProgress({ done: 0, total: files.length });
    try {
      for (let i = 0; i < files.length; i += 1) {
        await uploadDesignerStandard(files[i], subfolder, user.id, user.name || user.email || 'Unknown');
        setUploadProgress({ done: i + 1, total: files.length });
      }
      setStatus(`Uploaded ${files.length} file${files.length !== 1 ? 's' : ''}`);
    } catch (err) {
      console.error('[designerStandards] upload', err);
      window.alert(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploadingTo(null);
      setUploadProgress(null);
    }
  };

  const commitRename = async () => {
    if (!renameId) return;
    const item = items.find(i => i.id === renameId);
    const next = renameValue.trim();
    setRenameId(null);
    if (!item || !next || next === item.name) return;
    setBusyId(item.id);
    try {
      await renameDesignerStandard(item.id, next);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not rename.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (item: DesignerStandardItem) => {
    if (!canEdit) return;
    if (!window.confirm(`Remove “${item.name}” from Designer Standards on every project?`)) return;
    setBusyId(item.id);
    try {
      await deleteDesignerStandard(item.id);
      setStatus('Removed');
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not remove.');
    } finally {
      setBusyId(null);
    }
  };

  const move = async (item: DesignerStandardItem, subfolder: DesignerStandardSubfolder) => {
    if (!canEdit || subfolder === item.subfolder) return;
    setBusyId(item.id);
    try {
      await moveDesignerStandard(item.id, subfolder);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not move.');
    } finally {
      setBusyId(null);
    }
  };

  const nudge = async (subfolder: DesignerStandardSubfolder, list: DesignerStandardItem[], index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (!canEdit || target < 0 || target >= list.length) return;
    const ids = list.map(i => i.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setBusyId(list[index].id);
    try {
      await reorderDesignerStandards(subfolder, ids);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not reorder.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="settings-ops-section dss" aria-label="Designer Standards" data-tour="pf-designer-standards-settings">
      <h4 className="settings-ops-section-title">
        <BookMarked size={15} aria-hidden style={{ verticalAlign: '-3px', marginRight: 6 }} />
        Designer Standards
      </h4>
      <p className="setting-description">
        One shared library that appears read-only in the <strong>Designer Standards</strong> folder on every
        project’s Files tab (Project folders view). Any file type. Replacing a file here updates every
        project at once — nothing is copied per project.
        {total > 0 ? ` ${total} file${total !== 1 ? 's' : ''} published.` : ''}
      </p>
      {footer}
      {loadError ? <p className="dss-error">{loadError}</p> : null}
      {status ? (
        <p className="dss-status" role="status">
          {status}
        </p>
      ) : null}

      <div className="dss-grid">
        {bySub.map(sub => {
          const isUploading = uploadingTo === sub.id;
          return (
            <div
              key={sub.id}
              className={`dss-folder${dropTarget === sub.id ? ' dss-folder--drop' : ''}`}
              onDragOver={e => {
                if (!canEdit) return;
                e.preventDefault();
                setDropTarget(sub.id);
              }}
              onDragLeave={() => setDropTarget(cur => (cur === sub.id ? null : cur))}
              onDrop={e => {
                if (!canEdit) return;
                e.preventDefault();
                setDropTarget(null);
                void uploadFiles(sub.id, Array.from(e.dataTransfer.files || []));
              }}
            >
              <div className="dss-folder-head">
                <div>
                  <div className="dss-folder-title">{sub.label}</div>
                  <div className="dss-folder-hint">{SUBFOLDER_HINT[sub.id]}</div>
                </div>
                {canEdit ? (
                  <>
                    <input
                      ref={el => {
                        inputRefs.current[sub.id] = el;
                      }}
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={e => {
                        const list = Array.from(e.target.files || []);
                        e.target.value = '';
                        void uploadFiles(sub.id, list);
                      }}
                    />
                    <button
                      type="button"
                      className="dss-upload-btn"
                      disabled={!!uploadingTo}
                      onClick={() => inputRefs.current[sub.id]?.click()}
                    >
                      {isUploading ? (
                        <>
                          <Loader2 size={13} className="dss-spin" aria-hidden />
                          {uploadProgress ? `${uploadProgress.done}/${uploadProgress.total}` : 'Uploading…'}
                        </>
                      ) : (
                        <>
                          <Upload size={13} aria-hidden /> Upload
                        </>
                      )}
                    </button>
                  </>
                ) : null}
              </div>

              {sub.items.length === 0 ? (
                <p className="dss-empty">{canEdit ? 'Drop files here or use Upload.' : 'Nothing published yet.'}</p>
              ) : (
                <ul className="dss-list">
                  {sub.items.map((item, idx) => {
                    const busy = busyId === item.id;
                    return (
                      <li key={item.id} className="dss-row">
                        <div className="dss-row-main">
                          {renameId === item.id ? (
                            <input
                              className="dss-rename"
                              value={renameValue}
                              autoFocus
                              aria-label="File name"
                              onChange={e => setRenameValue(e.target.value)}
                              onBlur={() => void commitRename()}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  void commitRename();
                                }
                                if (e.key === 'Escape') setRenameId(null);
                              }}
                            />
                          ) : (
                            <a className="dss-name" href={item.url} target="_blank" rel="noopener noreferrer" title={item.name}>
                              {item.name}
                            </a>
                          )}
                          <span className="dss-meta">
                            {item.size ? `${formatSize(item.size)} · ` : ''}
                            {item.uploadedByName || 'Unknown'}
                            {item.uploadedAt.getTime() > 0 ? ` · ${item.uploadedAt.toLocaleDateString()}` : ''}
                          </span>
                        </div>
                        <div className="dss-row-acts">
                          {canEdit ? (
                            <>
                              <button
                                type="button"
                                className="dss-icon"
                                title="Move up"
                                aria-label={`Move ${item.name} up`}
                                disabled={busy || idx === 0}
                                onClick={() => void nudge(sub.id, sub.items, idx, -1)}
                              >
                                <ArrowUp size={13} />
                              </button>
                              <button
                                type="button"
                                className="dss-icon"
                                title="Move down"
                                aria-label={`Move ${item.name} down`}
                                disabled={busy || idx === sub.items.length - 1}
                                onClick={() => void nudge(sub.id, sub.items, idx, 1)}
                              >
                                <ArrowDown size={13} />
                              </button>
                              <select
                                className="dss-move"
                                value={item.subfolder}
                                disabled={busy}
                                aria-label={`Folder for ${item.name}`}
                                title="Move to another Designer Standards folder"
                                onChange={e => void move(item, e.target.value as DesignerStandardSubfolder)}
                              >
                                {DESIGNER_STANDARD_SUBFOLDERS.map(s => (
                                  <option key={s.id} value={s.id}>
                                    {s.label}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="dss-icon"
                                title="Rename"
                                aria-label={`Rename ${item.name}`}
                                disabled={busy}
                                onClick={() => {
                                  setRenameId(item.id);
                                  setRenameValue(item.name);
                                }}
                              >
                                <Pencil size={13} />
                              </button>
                            </>
                          ) : null}
                          <a className="dss-icon" href={item.url} download={item.name} target="_blank" rel="noopener noreferrer" title="Download">
                            <Download size={13} />
                          </a>
                          {canEdit ? (
                            <button
                              type="button"
                              className="dss-icon dss-icon--danger"
                              title="Remove"
                              aria-label={`Remove ${item.name}`}
                              disabled={busy}
                              onClick={() => void remove(item)}
                            >
                              {busy ? <Loader2 size={13} className="dss-spin" /> : <Trash2 size={13} />}
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default DesignerStandardsSettingsCard;
